import React, { ReactElement, useEffect, useState } from 'react';
import { NavLink } from 'react-router-dom';
import clsx from 'clsx';
import { SubIconProps } from './SideNavBar';

type SidebaarIconsProperties = {
  icondetails: {
    iconName: string;
    iconText: string;
    route: string;
    subRoutes?: SubIconProps[];
  };
  isChild?: boolean;
  onClickSubRouteExpand?: () => void;
  subRouteVisible?: boolean;
};

/**
 * SidebarIcon component displaying an icon and showing the icon name on hover.
 * @param  props - The properties for the SidebarIcon component.
 * @returns - SidebarIcon component.
 */
export default function SidebarIcons(props: SidebaarIconsProperties): ReactElement {
  const {
    icondetails: { iconName, iconText, route, subRoutes },
    isChild,
    onClickSubRouteExpand,
    subRouteVisible
  } = props;
  const [isActiveLink, setIsActiveLink] = useState(false);

  /**
   * Handler to set active route state.
   * @param isActive - Boolean value to check if the link is active.
   */
  const handleActiveRoute = (isActive: boolean): void => {
    setIsActiveLink(isActive);
  };

  /**
   * Hanldes expand button click.
   * @param e - Event.
   */
  const handleClick = (e: React.MouseEvent<HTMLButtonElement>): void => {
    e.preventDefault();
    e.stopPropagation();
    if (onClickSubRouteExpand) {
      onClickSubRouteExpand();
    }
  };

  return (
    <NavLink
      to={`/${route}`}
      className={({ isActive }) => {
        // This doesn't update state directly; it just handles className
        useEffect(() => {
          handleActiveRoute(isActive);
        }, [isActive]);

        return '';
      }}
    >
      <div className="text-gray-500 my-2 group/link">
        <div
          className={clsx('rounded cursor-pointer p-1.5 flex items-center w-full justify-between', {
            'text-gray-700 dark:text-gray-300 bg-primary-50 dark:bg-background-500':
              isActiveLink && !isChild,
            'group-hover/link:bg-primary-50 dark:group-hover/link:bg-background-500 dark:bg-background-700 text-gray-400 dark:text-gray-500 dark:group-hover/link:text-gray-300 group-hover/link:text-gray-700':
              !isActiveLink && !isChild
          })}
        >
          <div className="flex items-center">
            <span
              className={clsx(
                {
                  'material-icons': isActiveLink,
                  'material-icons-outlined': !isActiveLink,
                  'text-primary-500': isActiveLink && !isChild
                },
                'text-xl'
              )}
            >
              {iconName}
            </span>
            <p className="text-base font-medium pl-3 hidden group-hover/sidenav:block group-hover/sidenav">
              <span>{iconText}</span>
            </p>
          </div>
          {subRoutes && (
            <button
              onClick={(e) => handleClick(e)}
              type="button"
              className="material-icons-outlined text-base hidden group-hover/sidenav:block group-hover/sidenav hover:bg-gray-600/50 px-2 rounded"
            >
              {subRouteVisible ? 'keyboard_arrow_up' : 'keyboard_arrow_down'}
            </button>
          )}
        </div>
      </div>
    </NavLink>
  );
}
